import React, { useState, useEffect } from 'react';
import { Container, Row, Col, Card, Button, Modal, Form, Alert, Badge } from 'react-bootstrap';
import axios from 'axios';

const CategoryManagement = ({ restaurantId, menu, onMenuUpdate }) => {
  const [showModal, setShowModal] = useState(false);
  const [editingCategory, setEditingCategory] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  // Форма для категории
  const [formData, setFormData] = useState({
    name: '',
    description: ''
  });

  const token = localStorage.getItem('restaurantToken') || localStorage.getItem('managerToken');

  // Обработка изменений формы
  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  // Открыть модальное окно для создания
  const openCreateModal = () => {
    setFormData({
      name: '',
      description: ''
    });
    setEditingCategory(null);
    setShowModal(true);
    setError('');
    setSuccess('');
  };

  // Открыть модальное окно для редактирования
  const openEditModal = (category) => {
    setFormData({
      name: category.name,
      description: category.description || ''
    });
    setEditingCategory(category);
    setShowModal(true);
    setError('');
    setSuccess('');
  };

  // Сохранить категорию
  const handleSave = async () => {
    try {
      setLoading(true);
      setError('');

      // Валидация
      if (!formData.name.trim()) {
        setError('Название категории обязательно');
        return;
      }

      if (editingCategory) {
        // Обновление существующей категории
        console.log('Updating category:', editingCategory._id, formData);
        
        const response = await axios.put(
          `/api/menu/${restaurantId}/menus/${menu._id}/categories/${editingCategory._id}`,
          {
            name: formData.name.trim(),
            description: formData.description.trim()
          },
          {
            headers: { 'x-auth-token': token }
          }
        );
        
        console.log('Category update response:', response.data);
        onMenuUpdate(response.data);
        setSuccess('Категория обновлена успешно');
      } else {
        // Создание новой категории
        console.log('Creating new category:', formData);
        
        const response = await axios.post(
          `/api/menu/${restaurantId}/menus/${menu._id}/categories`,
          {
            name: formData.name.trim(),
            description: formData.description.trim(),
            order: menu.categories ? menu.categories.length : 0
          },
          {
            headers: { 'x-auth-token': token }
          }
        );
        
        console.log('Category creation response:', response.data);
        onMenuUpdate(response.data);
        setSuccess('Категория создана успешно');
      }

      setShowModal(false);
    } catch (error) {
      console.error('Error saving category:', error);
      setError('Ошибка при сохранении категории');
    } finally {
      setLoading(false);
    }
  };

  // Удалить категорию
  const handleDelete = async (categoryId) => {
    // Находим категорию для получения её названия
    const category = menu.categories.find(cat => cat._id === categoryId);
    const categoryName = category ? category.name : 'эту категорию';
    
    // Подсчитываем количество блюд в категории
    const itemsInCategory = getItemCountForCategory(categoryName);
    
    // Формируем сообщение с предупреждением
    let confirmMessage = `Вы уверены, что хотите удалить категорию "${categoryName}"?`;
    if (itemsInCategory > 0) {
      confirmMessage += `\n\n⚠️ ВНИМАНИЕ: Вместе с категорией будут удалены все ${itemsInCategory} блюд(а) из этой категории!`;
      confirmMessage += `\n\nЭто действие нельзя отменить.`;
    }
    
    if (window.confirm(confirmMessage)) {
      try {
        setLoading(true);
        console.log('Deleting category:', categoryId);
        
        // Сначала удаляем все блюда из этой категории
        if (itemsInCategory > 0) {
          console.log(`Deleting ${itemsInCategory} items from category "${categoryName}"`);
          
          // Получаем все блюда из этой категории
          const itemsToDelete = menu.items.filter(item => item.category === categoryName);
          
          // Удаляем каждое блюдо
          for (const item of itemsToDelete) {
            try {
              await axios.delete(
                `/api/menu/${restaurantId}/menus/${menu._id}/items/${item._id}`,
                {
                  headers: { 'x-auth-token': token }
                }
              );
              console.log(`Deleted item: ${item.name}`);
            } catch (itemError) {
              console.error(`Error deleting item ${item.name}:`, itemError);
              // Продолжаем удаление других блюд даже если одно не удалось
            }
          }
        }
        
        // Теперь удаляем саму категорию
        const response = await axios.delete(
          `/api/menu/${restaurantId}/menus/${menu._id}/categories/${categoryId}`,
          {
            headers: { 'x-auth-token': token }
          }
        );
        
        console.log('Category deletion response:', response.data);
        onMenuUpdate(response.data);
        
        // Формируем сообщение об успехе
        let successMessage = `Категория "${categoryName}" удалена успешно`;
        if (itemsInCategory > 0) {
          successMessage += ` вместе с ${itemsInCategory} блюд(ами)`;
        }
        setSuccess(successMessage);
      } catch (error) {
        console.error('Error deleting category:', error);
        setError('Ошибка при удалении категории');
      } finally {
        setLoading(false);
      }
    }
  };

  // Подсчет количества блюд в категории
  const getItemCountForCategory = (categoryName) => {
    if (!menu.items) return 0;
    return menu.items.filter(item => item.category === categoryName).length;
  };

  // Переместить категорию вверх
  const moveCategoryUp = async (categoryId) => {
    const categories = [...menu.categories];
    const currentIndex = categories.findIndex(cat => cat._id === categoryId);
    
    if (currentIndex > 0) {
      // Меняем местами с предыдущей категорией
      [categories[currentIndex], categories[currentIndex - 1]] = 
      [categories[currentIndex - 1], categories[currentIndex]];
      
      // Обновляем порядок
      const updatedCategories = categories.map((cat, index) => ({
        ...cat,
        order: index
      }));
      
      // Отправляем изменения на сервер
      try {
        setLoading(true);
        setError('');
        
        // Обновляем каждую категорию с новым порядком
        for (const category of updatedCategories) {
          await axios.put(
            `/api/menu/${restaurantId}/menus/${menu._id}/categories/${category._id}`,
            {
              name: category.name,
              description: category.description,
              order: category.order
            },
            {
              headers: { 'x-auth-token': token }
            }
          );
        }
        
        const updatedMenu = { ...menu, categories: updatedCategories };
        onMenuUpdate(updatedMenu);
        setSuccess('Порядок категорий обновлен');
        
        // Принудительно обновляем страницу через 1 секунду
        setTimeout(() => {
          window.location.reload();
        }, 1000);
      } catch (error) {
        console.error('Error updating category order:', error);
        setError('Ошибка при обновлении порядка категорий');
      } finally {
        setLoading(false);
      }
    }
  };

  // Переместить категорию вниз
  const moveCategoryDown = async (categoryId) => {
    const categories = [...menu.categories];
    const currentIndex = categories.findIndex(cat => cat._id === categoryId);
    
    if (currentIndex < categories.length - 1) {
      // Меняем местами со следующей категорией
      [categories[currentIndex], categories[currentIndex + 1]] = 
      [categories[currentIndex + 1], categories[currentIndex]];
      
      // Обновляем порядок
      const updatedCategories = categories.map((cat, index) => ({
        ...cat,
        order: index
      }));
      
      // Отправляем изменения на сервер
      try {
        setLoading(true);
        setError('');
        
        // Обновляем каждую категорию с новым порядком
        for (const category of updatedCategories) {
          await axios.put(
            `/api/menu/${restaurantId}/menus/${menu._id}/categories/${category._id}`,
            {
              name: category.name,
              description: category.description,
              order: category.order
            },
            {
              headers: { 'x-auth-token': token }
            }
          );
        }
        
        const updatedMenu = { ...menu, categories: updatedCategories };
        onMenuUpdate(updatedMenu);
        setSuccess('Порядок категорий обновлен');
        
        // Принудительно обновляем страницу через 1 секунду
        setTimeout(() => {
          window.location.reload();
        }, 1000);
      } catch (error) {
        console.error('Error updating category order:', error);
        setError('Ошибка при обновлении порядка категорий');
      } finally {
        setLoading(false);
      }
    }
  };

  return (
    <Container fluid>
      <Row>
        <Col>
          <Card>
            <Card.Header className="d-flex flex-column flex-md-row justify-content-between align-items-start align-items-md-center">
              <h4 className="mb-2 mb-md-0">Категории меню</h4>
              <Button variant="primary" onClick={openCreateModal} className="w-100 w-md-auto">
                Создать категорию
              </Button>
            </Card.Header>
            <Card.Body>
              {error && <Alert variant="danger">{error}</Alert>}
              {success && <Alert variant="success">{success}</Alert>}

              {loading && !menu.categories?.length ? (
                <div className="text-center">Загрузка...</div>
              ) : !menu ? (
                <Alert variant="warning">Меню не загружено</Alert>
              ) : (
                <div className="list-group">
                  {menu.categories
                    ?.sort((a, b) => (a.order ?? 0) - (b.order ?? 0))
                    ?.map((category, index) => (
                    <div key={category._id} className="list-group-item category-list-item">
                      {/* Мобильная версия */}
                      <div className="d-block d-md-none">
                        <div className="d-flex justify-content-between align-items-start mb-2">
                          <div className="d-flex align-items-center">
                            <Badge bg="secondary" className="category-order-badge me-2">
                              #{index + 1}
                            </Badge>
                            <div>
                              <strong className="d-block">{category.name}</strong>
                              <small className="text-muted">
                                {category.description || 'Без описания'}
                              </small>
                            </div>
                          </div>
                          <Badge bg="primary">
                            {getItemCountForCategory(category.name)} блюд
                          </Badge>
                        </div>
                        <div className="d-flex justify-content-center gap-1 flex-wrap">
                          <Button
                            variant="outline-secondary"
                            size="sm"
                            onClick={() => moveCategoryUp(category._id)}
                            disabled={index === 0}
                            title="Переместить вверх"
                          >
                            ↑
                          </Button>
                          <Button
                            variant="outline-secondary"
                            size="sm"
                            onClick={() => moveCategoryDown(category._id)}
                            disabled={index === menu.categories.length - 1}
                            title="Переместить вниз"
                          >
                            ↓
                          </Button>
                          <Button
                            variant="outline-primary"
                            size="sm"
                            onClick={() => openEditModal(category)}
                          >
                            Редактировать
                          </Button>
                          <Button
                            variant="outline-danger"
                            size="sm"
                            onClick={() => handleDelete(category._id)}
                          >
                            Удалить
                          </Button>
                        </div>
                      </div>
                      
                      {/* Десктопная версия */}
                      <div className="d-none d-md-flex justify-content-between align-items-center">
                        <div className="d-flex align-items-center category-info">
                          <Badge bg="secondary" className="category-order-badge me-3">
                            #{index + 1}
                          </Badge>
                          <div className="d-flex flex-column">
                            <strong className="mb-1">{category.name}</strong>
                            <small className="text-muted">
                              {category.description || 'Без описания'}
                            </small>
                          </div>
                          <Badge bg="primary" className="ms-auto me-3">
                            {getItemCountForCategory(category.name)} блюд
                          </Badge>
                        </div>
                        <div className="d-flex align-items-center gap-2">
                          <Button
                            variant="outline-secondary"
                            size="sm"
                            onClick={() => moveCategoryUp(category._id)}
                            disabled={index === 0}
                            title="Переместить вверх"
                          >
                            ↑
                          </Button>
                          <Button
                            variant="outline-secondary"
                            size="sm"
                            onClick={() => moveCategoryDown(category._id)}
                            disabled={index === menu.categories.length - 1}
                            title="Переместить вниз"
                          >
                            ↓
                          </Button>
                          <Button
                            variant="outline-primary"
                            size="sm"
                            onClick={() => openEditModal(category)}
                          >
                            Редактировать
                          </Button>
                          <Button
                            variant="outline-danger"
                            size="sm"
                            onClick={() => handleDelete(category._id)}
                          >
                            Удалить
                          </Button>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}

              {!loading && (!menu.categories || menu.categories.length === 0) && (
                <div className="text-center text-muted">
                  <p>Категории не найдены</p>
                  <p><small>Меню: {menu ? `ID ${menu._id}` : 'не загружено'}</small></p>
                  <Button variant="primary" onClick={openCreateModal}>
                    Создать первую категорию
                  </Button>
                </div>
              )}
            </Card.Body>
          </Card>
        </Col>
      </Row>

      {/* Модальное окно для создания/редактирования */}
      <Modal show={showModal} onHide={() => setShowModal(false)}>
        <Modal.Header closeButton>
          <Modal.Title>
            {editingCategory ? 'Редактировать категорию' : 'Создать категорию'}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form>
            <Form.Group className="mb-3">
              <Form.Label>Название категории *</Form.Label>
              <Form.Control
                type="text"
                name="name"
                value={formData.name}
                onChange={handleInputChange}
                placeholder="Например: Пицца"
                required
              />
            </Form.Group>
            
            <Form.Group className="mb-3">
              <Form.Label>Описание</Form.Label>
              <Form.Control
                as="textarea"
                rows={3}
                name="description"
                value={formData.description}
                onChange={handleInputChange}
                placeholder="Описание категории (опционально)"
              />
            </Form.Group>
          </Form>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowModal(false)}>
            Отмена
          </Button>
          <Button variant="primary" onClick={handleSave} disabled={loading}>
            {loading ? 'Сохранение...' : (editingCategory ? 'Обновить' : 'Создать')}
          </Button>
        </Modal.Footer>
      </Modal>
    </Container>
  );
};

export default CategoryManagement;